// ✅ src/pages/VehicleV2/TyreMaintenanceReport.jsx
// UPDATED FULL FILE
//
// ✅ Updated pattern similar to JobCardEntryDetailReport
// ✅ common localStorage / sessionStorage header pattern
// ✅ useMemo + useCallback + useRef pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / reset / refresh / pagination / print flow
// ✅ works with updated backend:
//    GET /api/tyreMaintenanceReport/meta?branchId=8
//    GET /api/tyreMaintenanceReport/list?branchId=&fromDate=&toDate=&serialId=&tyreTypeId=&vehicleId=&storeId=
//    GET /api/tyreMaintenanceReport/print?branchId=&fromDate=&toDate=&serialId=&tyreTypeId=&vehicleId=&storeId=

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
  FaEye,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/tyreMaintenanceReport";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const exportToCsv = (rows, filename = "tyre-maintenance-report.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const pick = (row, keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const createDefaultFilters = () => ({
  fromDate: "",
  toDate: "",
  serialId: 0,
  tyreTypeId: 0,
  vehicleId: 0,
  storeId: 0,
});

export default function TyreMaintenanceReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(() => createDefaultFilters(), []);

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     dropdown states
  =========================== */
  const [meta, setMeta] = useState({
    serials: [],
    tyreTypes: [],
    vehicles: [],
    stores: [],
  });

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);

  /* ===========================
     pagination
  =========================== */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalRows = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / itemsPerPage)),
    [totalRows]
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : "bg-white text-black"
      }`,
    [isDark]
  );

  const tdClass = useMemo(
    () =>
      `px-4 py-4 text-sm ${
        isDark ? "text-gray-300" : "text-gray-900"
      }`,
    [isDark]
  );

  const thClass = "px-4 py-4 font-semibold";

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* ===========================
     meta loaders
  =========================== */
  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchId: session.branchId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Meta load failed");
      }

      setMeta({
        serials: toList(data.serials),
        tyreTypes: toList(data.tyreTypes),
        vehicles: toList(data.vehicles),
        stores: toList(data.stores),
      });
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message || e.message || "Failed to load dropdowns"
      );
      setMeta({
        serials: [],
        tyreTypes: [],
        vehicles: [],
        stores: [],
      });
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchId, commonHeaders]);

  /* ===========================
     list fetch
  =========================== */
  const loadList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);

        const params = {
          branchId: session.branchId,
          fromDate: toStr(nextFilters.fromDate),
          toDate: toStr(nextFilters.toDate),
          serialId: toNum(nextFilters.serialId, 0),
          tyreTypeId: toNum(nextFilters.tyreTypeId, 0),
          vehicleId: toNum(nextFilters.vehicleId, 0),
          storeId: toNum(nextFilters.storeId, 0),
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "List load failed");
        }

        const list = toList(data.rows);
        setRows(list);
        setCurrentPage(1);
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load report"
        );
        setRows([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session.branchId, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setRows([]);
      setCurrentPage(1);

      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (["serialId", "tyreTypeId", "vehicleId", "storeId"].includes(name)) {
        setField(name, toNum(value, 0));
        return;
      }

      setField(name, value);
    },
    [setField]
  );

  const handleSearch = useCallback(async () => {
    await loadList(filters);
    toast.success("Report loaded");
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters();
    setFilters(reset);
    await loadList(reset);
    toast.info("Showing all records");
  }, [loadList]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await loadList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, loadList, defaultFilters]);

  const handleReset = useCallback(() => {
    setFilters(createDefaultFilters());
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(createDefaultFilters());
    toast.info("Filters cleared");
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAllPrint = useCallback(() => {
    navigate("/vehicle/TyreMaintenanceReport_Print", {
      state: {
        filters: {
          branchId: session.branchId,
          fromDate: toStr(filters.fromDate),
          toDate: toStr(filters.toDate),
          serialId: toNum(filters.serialId, 0),
          tyreTypeId: toNum(filters.tyreTypeId, 0),
          vehicleId: toNum(filters.vehicleId, 0),
          storeId: toNum(filters.storeId, 0),
        },
      },
    });

    toast.info("Printing all tyre maintenance records");
  }, [navigate, session.branchId, filters]);

  const handlePrintRow = useCallback(
    (row) => {
      const rowId = toNum(
        pick(row, ["fld_AddTyre_ID", "id", "AddTyreID"]),
        0
      );

      if (!rowId) {
        toast.error("Cannot print: Row ID not found");
        return;
      }

      navigate("/vehicle/TyreMaintenanceReport_Print", {
        state: {
          filters: {
            branchId: session.branchId,
            rowId,
            fromDate: toStr(filters.fromDate),
            toDate: toStr(filters.toDate),
            serialId: toNum(filters.serialId, 0),
            tyreTypeId: toNum(filters.tyreTypeId, 0),
            vehicleId: toNum(filters.vehicleId, 0),
            storeId: toNum(filters.storeId, 0),
          },
        },
      });
    },
    [navigate, session.branchId, filters]
  );

  const handleExport = useCallback(() => {
    try {
      if (!rows.length) {
        toast.info("No rows to export");
        return;
      }
      exportToCsv(rows);
      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows]);

  const goPage = useCallback(
    (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setCurrentPage(next);
    },
    [totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
      await loadList(defaultFilters);
    })();
  }, [loadPage, loadList, defaultFilters]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE MAINTENANCE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tyre Maintenance Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {totalRows}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isBusy
                      ? isDark
                        ? "bg-yellow-900 text-yellow-100"
                        : "bg-yellow-100 text-yellow-800"
                      : isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {pageLoading
                    ? "Loading Page"
                    : dropdownLoading
                    ? "Loading Dropdowns"
                    : listLoading
                    ? "Loading List"
                    : "Ready"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date</label>
                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Serial Number</label>
                      <select
                        name="serialId"
                        value={filters.serialId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value={0}>Select</option>
                        {meta.serials.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Number</label>
                      <select
                        name="vehicleId"
                        value={filters.vehicleId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value={0}>Select</option>
                        {meta.vehicles.map((v) => (
                          <option key={v.value} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Store</label>
                      <select
                        name="storeId"
                        value={filters.storeId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value={0}>Select</option>
                        {meta.stores.map((st) => (
                          <option key={st.value} value={st.value}>
                            {st.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Type</label>
                      <select
                        name="tyreTypeId"
                        value={filters.tyreTypeId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value={0}>Select</option>
                        {meta.tyreTypes.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-4 flex-wrap">
                      <button
                        onClick={handleReset}
                        type="button"
                        disabled={isBusy}
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        Search
                      </button>

                      <button
                        onClick={handleAllPrint}
                        type="button"
                        disabled={isBusy || rows.length === 0}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold ${
                          isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {currentItems.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Records Found
                    </h3>
                    <p
                      className={`mb-6 max-w-sm mx-auto ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Try adjusting your search criteria or click "View All".
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className={`${thClass} text-center`}>S.No</th>
                          <th className={`${thClass} text-left`}>Vehicle No</th>
                          <th className={`${thClass} text-left`}>Tyre Serial No</th>
                          <th className={`${thClass} text-left`}>Tyre Model</th>
                          <th className={`${thClass} text-left`}>Tyre Type</th>
                          <th className={`${thClass} text-left`}>Tyre Make</th>
                          <th className={`${thClass} text-left`}>Tyre Position</th>
                          <th className={`${thClass} text-left`}>Supplier</th>
                          <th className={`${thClass} text-right`}>Amount</th>
                          <th className={`${thClass} text-center`}>Actions</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {currentItems.map((r, idx) => {
                          const vehicleNo = pick(r, ["fld_vehicle_regno", "VehicleNo"]);
                          const tyreSerial = pick(r, ["fld_Tyreserialno", "fld_vehicle_serialno"]);
                          const tyreModel = pick(r, ["fld_TyreModel", "fld_tyre_model"]);
                          const tyreType = pick(r, ["fld_tyretypename", "fld_tyretype"]);
                          const tyreMake = pick(r, ["fld_TyreMake", "fld_tyremake"]);
                          const tyrePosition = pick(r, ["fld_vehicletyre_positiontype"]);
                          const supplier = pick(r, ["fld_Purchasefrom", "fld_suppliername"]);
                          const amount = pick(r, ["fld_TyreAmounts", "fld_tyreamt"]);

                          return (
                            <tr
                              key={`${pick(r, ["fld_AddTyre_ID", "id", "AddTyreID"]) || idx}-${idx}`}
                              className={`${
                                isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                              } transition-colors duration-200`}
                            >
                              <td className={`${tdClass} text-center`}>
                                {indexOfFirstItem + idx + 1}
                              </td>

                              <td className={`${tdClass} text-left`}>{vehicleNo || "-"}</td>
                              <td className={`${tdClass} text-left`}>{tyreSerial || "-"}</td>
                              <td className={`${tdClass} text-left`}>{tyreModel || "-"}</td>
                              <td className={`${tdClass} text-left`}>{tyreType || "-"}</td>
                              <td className={`${tdClass} text-left`}>{tyreMake || "-"}</td>
                              <td className={`${tdClass} text-left`}>{tyrePosition || "-"}</td>
                              <td className={`${tdClass} text-left`}>{supplier || "-"}</td>

                              <td className={`${tdClass} text-right font-medium`}>
                                {amount
                                  ? `₹${parseFloat(amount).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : "-"}
                              </td>

                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handlePrintRow(r)}
                                  disabled={isBusy}
                                  className={`p-2 rounded transition-colors ${
                                    isDark
                                      ? "bg-gray-700 text-white hover:bg-gray-600"
                                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title="Print"
                                  type="button"
                                >
                                  <FaPrint className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalRows > 0 && (
                <div className="mt-6 flex justify-center items-center gap-3">
                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || currentPage <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(currentPage - 1)}
                    disabled={isBusy || currentPage <= 1}
                    type="button"
                  >
                    Prev
                  </button>

                  <div
                    className={`px-4 py-2 border rounded ${
                      isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                    }`}
                  >
                    Page {currentPage} / {totalPages}
                  </div>

                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || currentPage >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(currentPage + 1)}
                    disabled={isBusy || currentPage >= totalPages}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}